import { notification } from 'antd';
import _ from 'lodash';
import { getIntl, history } from 'umi';

import env from '@/config/env';
import { apiRoute, getAll } from '@/services/api';
import SocketService from '@/services/socket.io';
import { sortFn } from '@/utils/infrastructure';
import { resetQueue } from '@/utils/infrastructure/communication';
import { getToken } from '@/utils/infrastructure/communication/request';
import { CoreEnums } from '@/utils/domain/constants';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  findAutomyUserId,
  getCurrentSession,
  setCurrentSession,
  setItem,
  removeItem,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
} from '@/utils/infrastructure/auth';
import { buildLoginUrl, normalizePortalSlug } from '@/utils/infrastructure/utilities';
import { isPortalAccessDenied } from './portalAuthErrors';
import { fetchPortalAuth } from './portalAuthFetch';
import { isCurrentPortal, mintPortalSession, switchToAutomySession } from './portalTransition';
import { menuIcons, routing } from './utils';

const initialState = {
  portal: null,
  menuIcons,
  retryTimes: 0,
  token: getToken({ global: true }),
};

const onPortalGateway = () =>
  typeof window !== 'undefined' && /^\/p\//.test(window.location.pathname);

const notifyPortalAccessDenied = (error) => {
  const intl = getIntl();
  const apiMessage = error?.apiMessage ? String(error.apiMessage).trim() : '';
  notification.error({
    message: intl.formatMessage({ id: 'portal.error.permission.title' }),
    description: apiMessage || intl.formatMessage({ id: 'portal.error.permission' }),
    duration: 8,
  });
};

const leaveDeniedPortalGateway = (shell) => {
  if (!onPortalGateway()) return;
  if (getCurrentSession()?.userId) {
    history.replace('/', shell);
    return;
  }
  history.replace(buildLoginUrl({ portalId: shell?.id, preserveRedirect: false }));
};

const applyLiveSession = () => {
  try {
    resetQueue();
  } catch (e) {}
  try {
    SocketService.getInstance().reconnectWithNewToken(env.SOCKET_CONNECTION_HUB);
  } catch (e) {}
};

function* applyPortalMenu({ call, put }) {
  const res = yield call(getAll, { route: 'processPortal/menu' });
  yield put({ type: 'queryMenu', payload: res });

  const hasNavigatedEmbed = sessionStorage.getItem('x-portal-embed-navigated');
  const hasNavigatedTemplate = sessionStorage.getItem('x-portal-template-navigated');
  const logoutInProgress = sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS);

  if ((!hasNavigatedEmbed || !hasNavigatedTemplate) && !logoutInProgress && res?.length > 0) {
    const topLevelItems = res.reduce((acc, section) => {
      if (section.menuItems) {
        return [...acc, ...section.menuItems.filter((item) => !item.parent)];
      }
      return acc;
    }, []);

    const toMenuSlug = (value) => _.kebabCase(value || '');
    const defaultTemplateItem = topLevelItems.find(
      (item) =>
        item.default === true &&
        item.type === CoreEnums.PortalMenuItemType.RenderTemplate &&
        item.templateLink,
    );
    const defaultEmbedItem = topLevelItems.find(
      (item) =>
        item.default === true &&
        item.type === CoreEnums.PortalMenuItemType.EmbedHtml &&
        item.embedHtmlLink,
    );

    if (!hasNavigatedTemplate && defaultTemplateItem) {
      const menuSlug = toMenuSlug(defaultTemplateItem.title);
      sessionStorage.setItem('x-portal-template-navigated', 'true');
      history.replace({
        pathname: `/r/${menuSlug}`,
      });
    } else if (!hasNavigatedEmbed && defaultEmbedItem) {
      const menuSlug = toMenuSlug(defaultEmbedItem.title);
      sessionStorage.setItem('x-portal-embed-navigated', 'true');
      history.replace({
        pathname: `/e/${menuSlug}`,
      });
    }
  }

  return res;
}

function* runEnterAutomy({ call, put }) {
  const result = yield call(switchToAutomySession);
  yield put({ type: 'cleanup' });
  if (result?.ok && result.minted?.accessToken) {
    yield put({
      type: 'auth/changeLoginStatus',
      payload: { ...result.minted, isPortal: false },
    });
    yield put({ type: 'auth/updateAuthorityAfterLogin', payload: result.minted });
  }
  if (result?.ok) {
    applyLiveSession();
    yield put({ type: 'user/fetchCurrent' });
    history.replace('/');
    return true;
  }
  if (result?.twoFactor) {
    history.replace('/user/login/two-factor');
    return false;
  }
  history.replace(buildLoginUrl({ preserveRedirect: false }));
  return false;
}

const Model = {
  namespace: 'companyPortal',
  state: initialState,
  effects: {
    *auth({ payload }, { call, put, select }) {
      try {
        // Validate required parameters before calling API
        let { companyCode, portalCode } = payload || {};

        // If codes are missing, try to recover from stored config
        if (!companyCode || !portalCode) {
          try {
            const storedConfig = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
            if (storedConfig) {
              const parsed = JSON.parse(storedConfig);
              companyCode = companyCode || parsed.companyCode;
              portalCode = portalCode || parsed.portalCode;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        companyCode = normalizePortalSlug(companyCode);
        portalCode = normalizePortalSlug(portalCode);

        // If still missing required codes, abort authentication
        if (!companyCode || !portalCode) {
          console.warn('Portal auth aborted: missing companyCode or portalCode');
          const retryTimes = yield select((o) => o.companyPortal.retryTimes);
          if (!retryTimes || retryTimes < 3) {
            yield put({ type: 'queryRetry', payload: 1 });
          }
          return;
        }

        // Check if we're switching to a different portal - clean up user context
        const currentPortal = yield select((o) => o.companyPortal.portal);
        const isSwitchingPortal =
          currentPortal &&
          (currentPortal.companyCode !== companyCode || currentPortal.portalCode !== portalCode);

        if (isSwitchingPortal) {
          // Clear user context and portal config when switching portals
          const currentPortalSession = getCurrentSession();
          if (currentPortalSession?.type === SESSION_TYPES.PORTAL) {
            removeItem(
              SESSION_TYPES.PORTAL,
              currentPortalSession.userId,
              currentPortalSession.resourceId,
              STORAGE_KEYS.USER_CONTEXT,
            );
            removeItem(
              SESSION_TYPES.PORTAL,
              currentPortalSession.userId,
              currentPortalSession.resourceId,
              STORAGE_KEYS.PORTAL_CONFIG,
            );
          }
        }

        // Public lookup only. Do not switch the session index here: copying
        // the Automy JWT into PORTAL and flipping the index makes refresh
        // send that Automy JWT with a portalId header, and the API responds
        // "parámetros de autenticación del portal de procesos son incorrectos".
        // CompanyPortal mints /auth/automy + portalId first, then switches.
        const config = yield call(fetchPortalAuth, companyCode, portalCode);
        const currentSession = getCurrentSession();
        const userId = currentSession?.userId;
        const portalId = config?.portal?.id || payload?.portalId;

        yield put({
          type: 'queryAuth',
          payload: { ...config, companyCode, portalCode, ...payload, userId, portalId },
        });
      } catch (e) {
        if (payload?.portalId && companyCode && portalCode) {
          const currentSession = getCurrentSession();
          yield put({
            type: 'queryAuth',
            payload: {
              portal: { id: payload.portalId },
              companyCode,
              portalCode,
              ...payload,
              userId: currentSession?.userId,
              portalId: payload.portalId,
            },
          });
          return;
        }
        const retryTimes = yield select((o) => o.companyPortal.retryTimes);
        if (!retryTimes || retryTimes < 3) {
          yield put({ type: 'queryRetry', payload: 1 });
        }
      }
    },

    *menu(o, { call, put }) {
      try {
        yield applyPortalMenu({ call, put });
      } catch (e) {}
    },

    *enter({ payload }, { call, put, select }) {
      let { companyCode, portalCode, portalId, kind } = payload || {};
      if (kind === 'automy' || portalCode === 'default') {
        return yield runEnterAutomy({ call, put });
      }

      companyCode = normalizePortalSlug(companyCode);
      portalCode = normalizePortalSlug(portalCode);
      if (!companyCode || !portalCode) return false;

      let lookup = null;
      if (!portalId) {
        try {
          lookup = yield call(fetchPortalAuth, companyCode, portalCode);
          portalId = lookup?.portal?.id;
        } catch (e) {
          yield put({ type: 'queryRetry', payload: 1 });
          return false;
        }
      }

      const session = getCurrentSession();
      const userId = session?.userId || findAutomyUserId();

      if (!userId || !portalId) {
        try {
          lookup = lookup || (yield call(fetchPortalAuth, companyCode, portalCode));
          portalId = portalId || lookup?.portal?.id;
        } catch (e) {}
        yield put({
          type: 'queryAuth',
          payload: { ...lookup, companyCode, portalCode, auto: true, portalId },
        });
        history.replace(buildLoginUrl({ portalId, preserveRedirect: false }));
        return false;
      }

      if (!isCurrentPortal(portalId)) {
        try {
          resetQueue();
        } catch (e) {}
        const result = yield call(mintPortalSession, userId, portalId);
        if (result?.twoFactor) {
          history.replace(
            buildLoginUrl({
              pathname: '/user/login/two-factor',
              portalId,
              preserveRedirect: false,
            }),
          );
          return false;
        }
        if (!result?.ok) {
          if (result?.error && isPortalAccessDenied(result.error)) {
            notifyPortalAccessDenied(result.error);
            leaveDeniedPortalGateway(yield select((state) => state.companyPortal.portal));
            return false;
          }
          yield put({ type: 'queryRetry', payload: 1 });
          return false;
        }
        yield put({
          type: 'auth/changeLoginStatus',
          payload: { ...result.minted, isPortal: true, portalId },
        });
        yield put({ type: 'auth/updateAuthorityAfterLogin', payload: result.minted });
      }

      try {
        lookup = lookup || (yield call(fetchPortalAuth, companyCode, portalCode));
      } catch (e) {
        lookup = { portal: { id: portalId } };
      }

      yield put({
        type: 'queryAuth',
        payload: {
          ...lookup,
          companyCode,
          portalCode,
          auto: true,
          userId,
          portalId,
        },
      });

      try {
        yield applyPortalMenu({ call, put });
      } catch (e) {}

      applyLiveSession();
      yield put({ type: 'resetRetry' });
      yield put({ type: 'user/fetchCurrent' });
      yield put({ type: 'settings/updatePortal' });

      const shell = yield select((state) => state.companyPortal.portal);
      if (onPortalGateway()) {
        history.replace('/', shell);
      }
      return true;
    },

    *logout({ payload }, { call, put, select }) {
      try {
        // Clean up local state immediately (don't wait for server)
        sessionStorage.removeItem('x-portal-embed-navigated');
        sessionStorage.removeItem('x-portal-template-navigated');
        removeSessionItem(STORAGE_KEYS.AUTHORITY);
        removeSessionItem(STORAGE_KEYS.AUTH_USER);
        removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
        if (!payload?.auto) {
          removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
        }

        if (payload?.isFailedLoginRedirect) {
          const portal = yield select((o) => o.companyPortal.portal);
          history.replace(buildLoginUrl({ portalId: portal?.id }), portal || history.location.state);
          // Fire logout to server in background (don't wait)
          apiRoute({ route: 'auth/logout', method: 'post' }).catch(() => {});
          return;
        }

        if (payload?.auto) {
          removeSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
          yield put({ type: 'reset' });
          // Do not POST /auth/logout: the bearer is the Automy session that
          // /auth/automy + portalId still needs. Logging out here invalidates
          // it and CompanyPortal then bounces to /user/login.
        } else {
          // For full logout: remove portal-prefixed config and clear session
          const currentSession = getCurrentSession();
          if (currentSession?.type === SESSION_TYPES.PORTAL) {
            removeItem(
              SESSION_TYPES.PORTAL,
              currentSession.userId,
              currentSession.resourceId,
              STORAGE_KEYS.PORTAL_CONFIG,
            );
            setCurrentSession(null);
          }

          // For non-auto logout, wait for server before re-authenticating
          yield call(apiRoute, { route: 'auth/logout', method: 'post' });
          yield put({ type: 'companyPortal/auth', payload });
        }
      } catch (e) {
        // Even if logout fails, continue with the flow
        if (payload?.auto) {
          yield put({ type: 'reset' });
        } else {
          const retryTimes = yield select((o) => o.companyPortal.retryTimes);
          if (!retryTimes || retryTimes < 3) {
            yield put({ type: 'queryRetry', payload: 1 });
          }
        }
      }
    },

    *enterAutomy(_, { call, put }) {
      return yield runEnterAutomy({ call, put });
    },

    *config({ payload }, { put }) {
      yield put({ type: 'queryConfig', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset', payload });
      // Restore company white-label shell after leaving the portal.
      yield put({ type: 'settings/updatePortal' });
    },
  },
  reducers: {
    queryAuth(state, { payload }) {
      const { portal, companyCode, portalCode, auto, userId, portalId } = payload;
      const config = { ...portal, companyCode, portalCode };

      // Write portal config to session-prefixed key if we have userId
      if (userId && portalId) {
        setItem(
          SESSION_TYPES.PORTAL,
          userId,
          String(portalId),
          STORAGE_KEYS.PORTAL_CONFIG,
          JSON.stringify(config),
        );
      } else {
        try {
          sessionStorage.setItem(STORAGE_KEYS.PORTAL_CONFIG, JSON.stringify(config));
        } catch (e) {}
      }

      sessionStorage.removeItem('x-portal-embed-navigated');
      sessionStorage.removeItem('x-portal-template-navigated');

      // Check if user is in 2FA flow
      const twoFactorRaw = getSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
      const isInTwoFactorFlow = twoFactorRaw ? JSON.parse(twoFactorRaw).required : false;

      const onLoginScreen =
        (typeof window !== 'undefined' && window.location.pathname.startsWith('/user/login')) ||
        (history.location?.pathname || '').startsWith('/user/login');

      if (
        !auto &&
        !sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS) &&
        !isInTwoFactorFlow &&
        !state.retryTimes &&
        !onLoginScreen
      ) {
        history.replace({ pathname: '/' }, config);
      }

      return {
        ...state,
        retryTimes: null,
        portal: config,
      };
    },

    queryMenu(state, { payload }) {
      const toMenuSlug = (value) => _.kebabCase(value || '');

      function menuItem(item) {
        const { path, id, ...more } = routing[item.type] || {};

        const hasChildren = item.subItems && item.subItems.length > 0;
        const orderedChildren = item.subItems?.sort(sortFn);
        const key = _.camelCase(item.title);
        const menuSlug = toMenuSlug(item.title || key);

        const fragments = id
          ? _(id)
              .split(',')
              .map((k) => item[k])
              .filter(Boolean)
              .join('/')
          : '';

        const fallbackPath =
          item.type === CoreEnums.PortalMenuItemType.EmbedHtml
            ? `/e/${menuSlug}`
            : item.type === CoreEnums.PortalMenuItemType.RenderTemplate
            ? `/r/${menuSlug}`
            : '';

        const resolvedPath = hasChildren
          ? `/${key}`
          : (path || item.link || fallbackPath || '') + fragments;

        return {
          ...more,
          name: item.title,
          key: _.camelCase(item.title),
          path: resolvedPath,
          children: _.map(orderedChildren, menuItem),
          type: item.type === CoreEnums.PortalMenuItemType.Link ? 'link' : item.type,
          embedHtmlLink: item.embedHtmlLink,
          templateLink: item.templateLink,
          menuSlug,
        };
      }

      const orderedItems = payload?.sort(sortFn)?.reduce((lhs, rhs) => {
        return _.concat(
          lhs,
          [
            rhs.menuItems && {
              type: 'session',
              title: rhs.title,
              icon: null,
            },
            ...(rhs.menuItems?.sort(sortFn) || []),
          ].filter(Boolean),
        );
      }, []);

      const menu = _.map(orderedItems, menuItem);

      try {
        const stored = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
        if (stored) {
          setSessionItem(
            STORAGE_KEYS.PORTAL_CONFIG,
            JSON.stringify({ ...JSON.parse(stored), menu }),
          );
        }
      } catch (e) {}

      return {
        ...state,
        portal: {
          ...state.portal,
          menu,
        },
      };
    },

    queryConfig(state, { payload }) {
      return {
        ...state,
        portal: payload,
      };
    },

    queryRetry(state, { payload }) {
      return {
        ...state,
        retryTimes: (state.retryTimes || 0) + payload,
      };
    },

    resetRetry(state) {
      return {
        ...state,
        retryTimes: 0,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
